import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useEffect, useRef } from "react";
import { ErroApi } from "./api.ts";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variante = "primario" | "secundario" | "perigo" | "fantasma";
const VAR: Record<Variante, string> = {
  primario: "bg-primary text-on-primary hover:bg-primary-strong",
  secundario: "bg-surface text-ink border border-line hover:bg-surface-2",
  perigo: "bg-danger text-on-danger hover:opacity-90",
  fantasma: "text-muted hover:bg-surface-2 hover:text-ink",
};
export function Botao({ variante = "secundario", className, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante }) {
  return (
    <button
      type="button"
      {...p}
      className={cx(
        "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-center text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50",
        VAR[variante],
        className,
      )}
    />
  );
}

const campo =
  "min-h-9 w-full min-w-0 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-ink placeholder:text-muted focus:border-primary focus:outline-none";
export const Entrada = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input {...p} className={cx(campo, className)} />;
export const Area = ({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...p} className={cx(campo, "font-mono text-xs leading-relaxed", className)} />
);
export const Selecao = ({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select {...p} className={cx(campo, className)} />;

export function Campo({
  rotulo,
  dica,
  erro,
  children,
  className,
}: {
  rotulo: string;
  dica?: ReactNode;
  erro?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: o controle vem em children
    <label className={cx("flex min-w-0 flex-col gap-1", className)}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">{rotulo}</span>
      {children}
      {erro ? <span className="text-xs text-danger">{erro}</span> : dica ? <span className="text-xs text-muted">{dica}</span> : null}
    </label>
  );
}

const TOM = {
  neutro: "bg-surface-2 text-muted",
  ok: "bg-primary-soft text-primary-strong",
  alerta: "bg-warning-soft text-warning",
  perigo: "bg-danger-soft text-danger",
  destaque: "bg-accent-soft text-accent-text",
} as const;
export const Selo = ({ tom = "neutro", children }: { tom?: keyof typeof TOM; children: ReactNode }) => (
  <span className={cx("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold", TOM[tom])}>
    {children}
  </span>
);

export const STATUS_FICHA: Record<string, { rotulo: string; tom: keyof typeof TOM }> = {
  rascunho: { rotulo: "Rascunho", tom: "neutro" },
  em_revisao: { rotulo: "Em revisão", tom: "destaque" },
  aprovada: { rotulo: "Aprovada", tom: "ok" },
  arquivada: { rotulo: "Arquivada", tom: "neutro" },
};
export const SeloStatus = ({ status }: { status: string }) => (
  <Selo tom={STATUS_FICHA[status]?.tom}>{STATUS_FICHA[status]?.rotulo ?? status}</Selo>
);

export const Cartao = ({
  titulo,
  acoes,
  children,
  className,
}: {
  titulo?: ReactNode;
  acoes?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section className={cx("flex min-w-0 flex-col gap-3 rounded-xl border border-line bg-surface p-4", className)}>
    {titulo || acoes ? (
      <div className="flex flex-wrap items-center justify-between gap-2">
        {titulo ? <h2 className="text-sm font-bold">{titulo}</h2> : <span />}
        {acoes}
      </div>
    ) : null}
    {children}
  </section>
);

export function Cabecalho({ titulo, sub, acoes }: { titulo: ReactNode; sub?: ReactNode; acoes?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-xl font-bold">{titulo}</h1>
        {sub ? <p className="text-sm text-muted">{sub}</p> : null}
      </div>
      {acoes ? <div className="flex flex-wrap gap-2">{acoes}</div> : null}
    </header>
  );
}

export const Tabela = ({ children }: { children: ReactNode }) => (
  <div className="overflow-x-auto rounded-xl border border-line bg-surface">
    <table className="w-full border-collapse text-sm [&_td]:border-t [&_td]:border-line [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-muted">
      {children}
    </table>
  </div>
);

export const Aviso = ({ tom = "perigo", children }: { tom?: "perigo" | "alerta" | "ok"; children: ReactNode }) => (
  <div
    role={tom === "perigo" ? "alert" : "status"}
    className={cx(
      "rounded-lg px-3 py-2 text-sm",
      tom === "perigo"
        ? "bg-danger-soft text-danger"
        : tom === "alerta"
          ? "bg-warning-soft text-warning"
          : "bg-primary-soft text-primary-strong",
    )}
  >
    {children}
  </div>
);

/** Estado de carregamento: indicador + texto, anunciado a leitores de tela. */
export const Carregando = ({ children = "Carregando…" }: { children?: ReactNode }) => (
  <p role="status" className="flex items-center gap-2 py-2 text-sm text-muted">
    <span className="size-4 animate-spin rounded-full border-2 border-primary-soft border-t-primary" aria-hidden="true" />
    {children}
  </p>
);

/** Falha ao carregar: 401/403 viram explicação, o resto mostra a mensagem do servidor. */
export function ErroCarga({ erro }: { erro: unknown }) {
  const texto =
    erro instanceof ErroApi && erro.status === 403
      ? "Seu papel não tem acesso a esta página."
      : erro instanceof ErroApi && erro.status === 404
        ? "Não encontrado."
        : erro instanceof Error
          ? `Não foi possível carregar: ${erro.message}`
          : "Não foi possível carregar.";
  return <Aviso>{texto}</Aviso>;
}

export const Vazio = ({ children }: { children: ReactNode }) => (
  <p className="rounded-xl border border-dashed border-line p-6 text-center text-sm text-muted">{children}</p>
);

/** Diálogo nativo: foco preso e Esc fecham sem biblioteca. */
export function Dialogo({
  aberto,
  aoFechar,
  titulo,
  children,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);
  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      className="m-auto w-[min(560px,calc(100vw-32px))] rounded-xl border border-line bg-surface p-0 text-fg backdrop:bg-scrim"
    >
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-bold">{titulo}</h2>
          <Botao variante="fantasma" onClick={aoFechar} aria-label="Fechar">
            ×
          </Botao>
        </div>
        {children}
      </div>
    </dialog>
  );
}

export const dataHora = (v: string | number | Date | null | undefined) =>
  v ? new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export { cx };
